// Admin "Tools" menu for the top navbar. Replaces the long row of admin icon buttons with:
//  - one Tools button that opens every admin tool, grouped, with a search box
//  - up to 6 pinned tools (★) shown as icons in the bar, saved per user in this browser
//  - Ctrl/⌘ + K quick search from any page
// Permission lists mirror the ones in Navbar.js. Keep them in sync.
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  LayoutGrid, Search, Star, X, ChevronDown, Shield, Eye, ClipboardList, CreditCard, Bell, IndianRupee,
  Globe, ShoppingCart, Boxes, Package, Wrench, Users, ScanLine, PackageCheck, BarChart3, Activity, Tag,
  BookOpen, DollarSign, Truck, Mail, MessageCircle, BarChart2, Database, Sparkles, FileSpreadsheet, FileText, Share2,
  GitBranch,
} from 'lucide-react';

const DOC_SHARING_ADMINS = ['9811117751', '9999313175'];
const PRICE_MASTER_ADMINS = ['9811117751', '9999313175'];
const ANALYTICS_ADMINS = ['9999313175', '9811117751'];
const WAREHOUSE_2_ADMINS = ['9999313175', '9811117751', '9643558419'];
const STOCK_TRAIL_ADMINS = ['9999313175', '9811117751', '9643558419'];
const COUPON_MASTER_ADMINS = ['9999313175', '9811117751', '9643558419'];
const OWNER = '9999313175';

// Always in the top bar for anyone who has access to them (cannot be unpinned):
// 2026-02-28 · owner feedback — surface Customer ledgers, Document sharing (with
// pending badge) and Pending POs as fixed shortcuts on BOTH desktop and mobile.
// Each is still permission-gated inside buildTools, so users without access
// simply won't see them (`.find(t => t.n === n)` returns undefined → skipped).
const FIXED = [
  'Task manager', 'Admin panel', 'Cheque manager', 'WhatsApp hub',
  'Customer ledgers', 'Document sharing', 'Pending POs to share',
];
// Extra tools the user can pin with the star (starts with Payments).
const DEFAULT_PINS = ['Payments'];

export function buildTools({ mobile, pendingDocCount }) {
  const m = String(mobile || '');
  const has = (list) => list.includes(m);
  const groups = [
    { g: 'Daily', c: '#d97706', items: [
      { n: 'Admin panel', p: '/admin', I: Shield },
      { n: 'God view', p: '/admin/god-mode', I: Eye },
      { n: 'Task manager', p: '/task-manager', I: ClipboardList },
      { n: 'Payments', p: '/admin/payments', I: CreditCard },
      { n: 'Orders', p: '/admin/orders', I: Bell },
      { n: 'Price requests', p: '/admin/price-requests', I: IndianRupee },
    ] },
    { g: 'Panels', c: '#2563eb', items: [
      { n: 'Nepal distributor', p: '/distributor', I: Globe },
      { n: 'Domestic distributor', p: '/dist', I: LayoutGrid },
      { n: 'Salesman panel', p: '/salesman', I: ShoppingCart },
      { n: 'Warehouse head', p: '/warehouse-head', I: Boxes },
      { n: 'Warehouse manager', p: '/warehouse', I: Package },
      { n: 'Mechanic panel', p: '/mechanic', I: Wrench },
      { n: 'Staff panel', action: 'staff', I: Users },
    ] },
    { g: 'Warehouse', c: '#c026d3', items: [
      has(WAREHOUSE_2_ADMINS) && { n: 'Warehouse 2.0 (scan ops)', p: '/admin/warehouse-2', I: ScanLine },
      { n: 'Warehouse setup', p: '/warehouse-setup', I: Boxes },
      { n: 'Warehouse management', p: '/warehouse-ops', I: PackageCheck },
      { n: 'Warehouse intel', p: '/warehouse-intel', I: BarChart3 },
      has(STOCK_TRAIL_ADMINS) && { n: 'Stock trail hub', p: '/warehouse/stock-trail', I: Activity },
      m === OWNER && { n: 'Migrations panel', p: '/admin/data-migrations', I: GitBranch, k: 'migration migrations data-migrations stock fix catch-up catch up heal' },
      { n: 'All products (dead stock)', p: '/admin/all-products', I: Package },
    ] },
    { g: 'Money & pricing', c: '#059669', items: [
      { n: 'Cheque manager', p: '/cheque-manager', I: CreditCard },
      has(PRICE_MASTER_ADMINS) && { n: 'Price master', p: '/price-master', I: IndianRupee },
      has(COUPON_MASTER_ADMINS) && { n: 'Coupon & MOQ master', p: '/admin/coupon-master', I: Tag },
      has(DOC_SHARING_ADMINS) && { n: 'Customer ledgers', p: '/admin/ledger', I: BookOpen },
      m === OWNER && { n: 'API costs', p: '/admin/api-costs', I: DollarSign },
    ] },
    { g: 'Sharing & data', c: '#0891b2', items: [
      has(DOC_SHARING_ADMINS) && { n: 'NTO procurement', p: '/nto', I: Truck },
      has(ANALYTICS_ADMINS) && { n: 'Pending POs to share', p: '/nto?subtab=purchase-orders&po=pending', I: Share2 },
      has(DOC_SHARING_ADMINS) && { n: 'Document sharing', p: '/doc-sharing', I: Mail, badge: pendingDocCount },
      m === OWNER && { n: 'WhatsApp hub', p: '/admin/whatsapp-hub', I: MessageCircle },
      has(ANALYTICS_ADMINS) && { n: 'Analytics', p: '/analytics', I: BarChart2 },
      has(ANALYTICS_ADMINS) && { n: 'Tally integration', p: '/tally-integration', I: Database },
      { n: 'Customer convenience panel', p: '/customer-quotations', I: Sparkles },
      { n: 'Excel import', action: 'excel', I: FileSpreadsheet },
      { n: 'Create new item post', p: '/posters/new', I: FileText },
    ] },
  ];
  return groups.map(G => ({ ...G, items: G.items.filter(Boolean).map(t => ({ ...t, g: G.g, c: G.c })) }));
}

const readPins = (key) => { try { const v = JSON.parse(localStorage.getItem(key) || 'null'); return Array.isArray(v) ? v : DEFAULT_PINS; } catch (e) { return DEFAULT_PINS; } };

export default function AppToolsMenu({ user, pendingDocCount = 0, onExcelImport, onStaffPanel, compact = false, showPins = true }) {
  const navigate = useNavigate();
  const groups = useMemo(() => buildTools({ mobile: user?.mobile, pendingDocCount }), [user?.mobile, pendingDocCount]);
  const all = useMemo(() => groups.flatMap(G => G.items), [groups]);
  const pinKey = `jp_nav_pins_${user?.mobile || 'me'}`;
  const [pins, setPins] = useState(() => readPins(pinKey));
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const [palette, setPalette] = useState(false);
  const [pq, setPq] = useState('');
  const [sel, setSel] = useState(0);
  const inputRef = useRef(null);
  const pRef = useRef(null);

  useEffect(() => { setPins(readPins(pinKey)); }, [pinKey]);
  const togglePin = (n) => setPins(prev => {
    const next = prev.includes(n) ? prev.filter(x => x !== n) : [...prev, n].slice(-4);
    try { localStorage.setItem(pinKey, JSON.stringify(next)); } catch (e) {}
    return next;
  });
  const run = (t) => {
    setOpen(false); setPalette(false);
    if (t.action === 'excel') return onExcelImport && onExcelImport();
    if (t.action === 'staff') return onStaffPanel && onStaffPanel();
    navigate(t.p);
  };

  // Ctrl/⌘ K opens quick search; Esc closes. Only the desktop instance listens.
  useEffect(() => {
    if (compact) return undefined;
    const onKey = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'k') { e.preventDefault(); setOpen(false); setPalette(true); setPq(''); setSel(0); }
      else if (e.key === 'Escape') { setOpen(false); setPalette(false); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [compact]);
  useEffect(() => { if (open) setTimeout(() => inputRef.current && inputRef.current.focus(), 30); }, [open]);
  useEffect(() => { if (palette) setTimeout(() => pRef.current && pRef.current.focus(), 30); }, [palette]);

  const filt = (s) => { const k = s.trim().toLowerCase(); return k ? all.filter(t => t.n.toLowerCase().includes(k) || t.g.toLowerCase().includes(k) || (t.k || '').toLowerCase().includes(k)) : all; };
  const pages = [{ n: 'Home', p: '/', I: LayoutGrid, g: 'Page' }, { n: 'Categories', p: '/categories', I: LayoutGrid, g: 'Page' }, { n: 'Catalog', p: '/catalog', I: BookOpen, g: 'Page' }, { n: 'My orders', p: '/my-orders', I: Package, g: 'Page' }, { n: 'Cart', p: '/cart', I: ShoppingCart, g: 'Page' }];
  const pList = (() => {
    const k = pq.trim().toLowerCase();
    const base = [...pages, ...all].filter(t => !k || t.n.toLowerCase().includes(k) || (t.k || '').toLowerCase().includes(k));
    if (k && /\d/.test(k)) base.unshift({ n: `Search catalog for "${pq.trim()}"`, p: `/catalog?search=${encodeURIComponent(pq.trim())}`, I: Search, g: 'Part' });
    return base.slice(0, 12);
  })();
  const waiting = all.some(t => t.badge > 0);

  const Item = ({ t }) => (
    <div role="menuitem" tabIndex={0} onClick={() => run(t)} onKeyDown={(e) => { if (e.key === 'Enter') run(t); }}
      className="group flex items-center gap-2.5 px-2 py-1.5 rounded-lg cursor-pointer hover:bg-gray-100 dark:hover:bg-slate-800 text-sm text-gray-800 dark:text-gray-100" data-testid={`tool-${t.n.replace(/\W+/g, '-').toLowerCase()}`}>
      <span className="w-8 h-8 rounded-md flex items-center justify-center border border-gray-200 dark:border-slate-700 flex-shrink-0" style={{ color: t.c }}><t.I className="w-4 h-4" /></span>
      <span className="flex-1 min-w-0 leading-tight">{t.n}</span>
      {t.badge > 0 && <span className="text-[10px] font-bold bg-red-500 text-white rounded-full px-1.5">{t.badge > 99 ? '99+' : t.badge}</span>}
      {FIXED.includes(t.n) ? (
        <span className="p-1 text-amber-500" title="Always in the top bar"><Star className="w-3.5 h-3.5" fill="currentColor" /></span>
      ) : (
      <button type="button" aria-label={`Pin ${t.n}`} title="Pin to top bar" onClick={(e) => { e.stopPropagation(); togglePin(t.n); }}
        className={`p-1 rounded ${pins.includes(t.n) ? 'text-amber-500' : 'text-gray-400 opacity-0 group-hover:opacity-100'} hover:text-amber-500`}>
        <Star className="w-3.5 h-3.5" fill={pins.includes(t.n) ? 'currentColor' : 'none'} />
      </button>
      )}
    </div>
  );

  const shown = filt(q);
  return (
    <>
      {showPins && (
        <div className={compact ? 'flex items-center gap-0.5' : 'hidden md:flex items-center gap-0.5'} data-testid={compact ? 'mobile-nav-shortcuts' : 'nav-pins'}>
          {[...FIXED, ...(compact ? [] : pins.filter(n => !FIXED.includes(n)))].map(n => all.find(t => t.n === n)).filter(Boolean).map(t => (
            <button key={t.n} type="button" title={t.n} aria-label={t.n} onClick={() => run(t)} className="relative h-8 w-8 rounded-md flex items-center justify-center hover:bg-gray-100 dark:hover:bg-slate-800" style={{ color: t.c }}>
              <t.I className="w-4 h-4" />
              {t.badge > 0 && <span className="absolute -top-1 -right-1 h-4 min-w-[16px] px-1 bg-red-500 text-white text-[9px] font-bold rounded-full flex items-center justify-center">{t.badge > 99 ? '99+' : t.badge}</span>}
            </button>
          ))}
        </div>
      )}
      {!compact && (
        <button type="button" onClick={() => { setOpen(false); setPalette(true); setPq(''); setSel(0); }} title="Go to any tool (Ctrl K)"
          className="hidden xl:flex items-center gap-2 h-8 px-2.5 rounded-md border border-gray-200 dark:border-slate-700 text-gray-500 text-xs hover:border-gray-400" data-testid="nav-goto">
          <Search className="w-3.5 h-3.5" />Go to…<kbd className="ml-1 font-mono text-[10px] border border-gray-200 dark:border-slate-700 rounded px-1">Ctrl K</kbd>
        </button>
      )}
      <button type="button" onClick={() => { setPalette(false); setOpen(o => !o); setQ(''); }} aria-expanded={open} aria-haspopup="true"
        className={compact
          ? 'relative h-9 w-9 rounded-md border border-gray-300 dark:border-slate-700 flex items-center justify-center text-gray-700 dark:text-gray-200'
          : `relative flex items-center gap-1.5 h-8 px-2.5 rounded-md border text-xs font-semibold ${open ? 'border-amber-500 text-amber-700' : 'border-gray-200 dark:border-slate-700 text-gray-700 dark:text-gray-200 hover:border-gray-400'}`}
        data-testid={compact ? 'mobile-tools-btn' : 'nav-tools-btn'} title="All tools">
        <LayoutGrid className="w-4 h-4" />{!compact && <>Tools<ChevronDown className="w-3.5 h-3.5" /></>}
        {waiting && <span className="absolute -top-1 -right-1 w-2.5 h-2.5 rounded-full bg-red-500 ring-2 ring-white dark:ring-slate-900" />}
      </button>

      {open && (
        <>
          <div className="fixed inset-0 z-[60] bg-black/30" onClick={() => setOpen(false)} />
          <div role="menu" aria-label="All tools" data-testid="tools-panel"
            className={`fixed z-[61] bg-white dark:bg-slate-900 border border-gray-200 dark:border-slate-700 shadow-2xl overflow-auto ${compact
              ? 'inset-x-0 bottom-0 top-14 rounded-t-2xl p-3'
              : 'left-1/2 -translate-x-1/2 top-[70px] w-[min(1100px,calc(100vw-32px))] max-h-[calc(100vh-90px)] rounded-2xl p-4'}`}>
            <div className="flex items-center gap-2 mb-3">
              <div className="flex-1 flex items-center gap-2 h-10 px-3 rounded-lg border border-gray-300 dark:border-slate-700 focus-within:border-amber-500">
                <Search className="w-4 h-4 text-gray-400" />
                <input ref={inputRef} value={q} onChange={(e) => setQ(e.target.value)} placeholder="Find a tool, e.g. cheque, tally, warehouse" className="flex-1 bg-transparent outline-none text-sm text-gray-900 dark:text-gray-100" />
              </div>
              <span className="hidden md:inline text-xs text-gray-500">★ pins a tool to the top bar</span>
              <button type="button" onClick={() => setOpen(false)} className="h-9 w-9 rounded-md flex items-center justify-center hover:bg-gray-100 dark:hover:bg-slate-800" aria-label="Close"><X className="w-4 h-4" /></button>
            </div>
            {shown.length === 0 && <div className="text-center text-sm text-gray-500 py-6">No tool matches that.</div>}
            <div className={`grid gap-3 ${compact ? 'grid-cols-1' : 'grid-cols-2 md:grid-cols-3 xl:grid-cols-5'}`}>
              {groups.map(G => {
                const its = shown.filter(t => t.g === G.g);
                if (!its.length) return null;
                return (
                  <div key={G.g}>
                    <div className="text-[11px] font-semibold uppercase tracking-wider text-gray-500 px-2 mb-1">{G.g}</div>
                    {its.map(t => <Item key={t.n} t={t} />)}
                  </div>
                );
              })}
            </div>
            {!compact && <div className="mt-3 pt-3 border-t border-gray-200 dark:border-slate-700 text-xs text-gray-500">Ctrl K opens quick search from any page. Pinned tools stay in your top bar.</div>}
          </div>
        </>
      )}

      {palette && (
        <>
          <div className="fixed inset-0 z-[60] bg-black/40" onClick={() => setPalette(false)} />
          <div role="dialog" aria-label="Go to" className="fixed z-[61] left-1/2 -translate-x-1/2 top-[12vh] w-[min(620px,calc(100vw-32px))] bg-white dark:bg-slate-900 border border-gray-200 dark:border-slate-700 rounded-2xl shadow-2xl overflow-hidden" data-testid="goto-palette">
            <input ref={pRef} value={pq} onChange={(e) => { setPq(e.target.value); setSel(0); }} placeholder="Go to… (tool, page or part code)"
              onKeyDown={(e) => {
                if (e.key === 'ArrowDown') { e.preventDefault(); setSel(s => Math.min(s + 1, pList.length - 1)); }
                else if (e.key === 'ArrowUp') { e.preventDefault(); setSel(s => Math.max(s - 1, 0)); }
                else if (e.key === 'Enter' && pList[sel]) { run(pList[sel]); }
              }}
              className="w-full h-14 px-4 text-base bg-transparent outline-none border-b border-gray-200 dark:border-slate-700 text-gray-900 dark:text-gray-100" />
            <ul className="max-h-[52vh] overflow-auto p-2">
              {pList.map((t, k) => (
                <li key={t.n} onClick={() => run(t)} onMouseEnter={() => setSel(k)}
                  className={`flex items-center gap-3 px-3 py-2 rounded-lg cursor-pointer text-sm ${k === sel ? 'bg-amber-50 text-amber-800 dark:bg-amber-500/10 dark:text-amber-300' : 'text-gray-800 dark:text-gray-100'}`}>
                  <t.I className="w-4 h-4" />{t.n}<span className="ml-auto text-[11px] text-gray-400 font-mono">{t.g}</span>
                </li>
              ))}
              {pList.length === 0 && <li className="px-3 py-2 text-sm text-gray-500">No match</li>}
            </ul>
          </div>
        </>
      )}
    </>
  );
}
